import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Check, Rocket } from 'lucide-react';
import { RELEASE_NOTES_HISTORY } from '@/hooks/useVersionCheck';
import { ScrollArea } from '@/components/ui/scroll-area';

interface ReleaseNotesDialogProps {
  open: boolean;
  onClose: () => void;
  previousVersion?: string | null;
}

export function ReleaseNotesDialog({ open, onClose, previousVersion }: ReleaseNotesDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center">
              <Rocket className="h-6 w-6 text-primary-foreground" />
            </div>
            <div>
              <DialogTitle className="text-xl">Changelog</DialogTitle>
              <DialogDescription className="flex items-center gap-2 mt-1">
                <span className="text-xs text-muted-foreground">
                  Alle Versionen und Änderungen
                </span>
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        
        <ScrollArea className="flex-1 pr-4 -mr-4">
          <div className="space-y-6 py-2">
            {RELEASE_NOTES_HISTORY.map((release, releaseIndex) => (
              <div key={release.version} className="space-y-3">
                <div className="flex items-center gap-2 sticky top-0 bg-background py-2">
                  <Badge 
                    variant={releaseIndex === 0 ? "default" : "secondary"} 
                    className="text-xs"
                  >
                    v{release.version}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {new Date(release.date).toLocaleDateString('de-DE')}
                  </span>
                  {releaseIndex === 0 && previousVersion && (
                    <Badge variant="outline" className="text-xs">
                      Neu
                    </Badge>
                  )}
                </div>
                
                <h4 className="text-sm font-medium">{release.title}</h4>
                
                <div className="space-y-2">
                  {release.changes.map((change, changeIndex) => (
                    <div 
                      key={changeIndex} 
                      className="flex items-start gap-3 p-2.5 rounded-lg bg-muted/50 hover:bg-muted/80 transition-colors"
                    >
                      <Sparkles className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
                      <span className="text-sm leading-relaxed">{change}</span>
                    </div>
                  ))}
                </div>
                
                {releaseIndex < RELEASE_NOTES_HISTORY.length - 1 && (
                  <div className="border-b pt-2" />
                )}
              </div>
            ))}
          </div>
        </ScrollArea>

        <DialogFooter className="pt-4">
          <Button onClick={onClose} className="w-full gap-2">
            <Check className="h-4 w-4" />
            Schließen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
